import { useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { format } from 'date-fns';

// material-ui
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';

// ant design icons
import HistoryOutlined from '@ant-design/icons/HistoryOutlined';
import RobotOutlined from '@ant-design/icons/RobotOutlined';
import UserOutlined from '@ant-design/icons/UserOutlined';

import useOnlineStatus from 'hooks/useOnlineStatus';
import { getTargetStatusHistory } from 'services/api/iep';
import { PROMPT_LEVELS } from 'utils/promptLevels';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const PHASE_LABELS = {
  not_introduced: 'Not introduced',
  daily_tracking: 'Daily tracking',
  weekly_maintenance: 'Weekly maintenance',
  monthly: 'Monthly maintenance',
  long_term: 'Long-term maintenance'
};

const SHEET_LABELS = { cold_probe: 'Cold Probe', multi_trial: 'Multi-Trial', maintenance_probe: 'Maintenance Probe' };

const SOURCE_LABELS = {
  target: 'a target-specific override',
  goal: 'a goal override',
  child: "this child's override",
  global: 'the global settings',
  default: 'the default rules'
};

// Rule values a target can set for itself (Edit Target), in plain English.
const TARGET_RULE_LABELS = {
  cold_probe_consecutive: (v) => `Cold Probe ${v} consecutive sessions`,
  multi_trial_pct: (v) => `Multi-Trial ${v}%`,
  multi_trial_consecutive: (v) => `Multi-Trial ${v} consecutive sessions`
};

/** "Rules from ..." - names the target's own values when it has any. */
const rulesSource = (d) => {
  const own = d.target_rules && Object.keys(d.target_rules).length > 0 ? d.target_rules : null;
  if (!own) return `Rules from ${SOURCE_LABELS[d.criteria_source] || d.criteria_source}`;
  const list = Object.entries(own)
    .map(([k, v]) => (TARGET_RULE_LABELS[k] ? TARGET_RULE_LABELS[k](v) : `${k} ${v}`))
    .join(', ');
  const rest = SOURCE_LABELS[d.base_source] || d.base_source;
  return `Set on this target: ${list}${rest ? ` · other rules from ${rest}` : ''}`;
};

const promptLabel = (code) => {
  const p = PROMPT_LEVELS.find((x) => x.code === code);
  return p ? `${p.code} (${p.label})` : code || 'IND';
};

/** The server stores UTC without a zone; show it in the viewer's local time with its zone. */
const formatWhen = (iso) => {
  if (!iso) return '—';
  const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return iso;
  const zone = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' }).formatToParts(d).find((p) => p.type === 'timeZoneName');
  return `${format(d, 'yyyy-MM-dd HH:mm')}${zone ? ` ${zone.value}` : ''}`;
};

const formatValue = (v) => {
  if (typeof v === 'number') return `${v}%`;
  if (v === 'plus') return '+';
  if (v === 'minus') return '−';
  if (v === 'na') return 'NA';
  return String(v ?? '—');
};

/** One plain-English sentence for why the rule engine made this change. */
const explain = (d) => {
  if (d.mode === 'recheck') {
    const rule = d.rule === 'not_met' ? '' : ` It meets the new rule: ${explainRule(d)}`;
    return d.outcome === 'still_mastered'
      ? `The mastery rules were changed. This target was re-checked against the new rules and is still mastered.${rule}`
      : 'The mastery rules were changed. This target was re-checked against the new rules and still does not meet them.';
  }
  return explainRule(d);
};

/** The rule that fired, in plain English. */
const explainRule = (d) => {
  const c = d.criteria || {};
  const level = promptLabel(d.prompt_level);
  const ev = d.evidence || [];
  const plus = ev.filter((e) => e.value === 'plus').length;
  switch (d.rule) {
    case 'cold_probe':
      return `${c.cold_probe_consecutive} consecutive "+" on Cold Probe, each at ${level} or with less help.`;
    case 'multi_trial':
      return `${c.multi_trial_consecutive} consecutive Multi-Trial sessions at ${c.multi_trial_pct}% or more, counting only trials at ${level} or with less help.`;
    case 'weekly_met':
      return `${plus} of ${ev.length} weekly maintenance probes were "+" (${c.weekly_required_plus} needed) at ${level} or with less help.`;
    case 'weekly_not_met':
      return `${ev.length} weekly maintenance probes without ${c.weekly_required_plus} "+", so the target went back to daily tracking.`;
    case 'monthly_met':
      return `A "+" was recorded on the monthly maintenance probe at ${level} or with less help.`;
    case 'monthly_not_met':
      return `2 monthly maintenance probes without a "+", so the target went back to weekly maintenance.`;
    case 'not_met':
      return 'The mastery rules were changed, and under the new rules this target no longer meets the mastery criteria.';
    case 'status_sync':
      return 'The status was updated to match the target’s current maintenance phase.';
    default:
      return d.rule || '';
  }
};

const WhyPanel = ({ details }) => {
  const theme = useTheme();
  const ev = details.evidence || [];
  return (
    <Box sx={{ mt: 1, p: 1.5, borderRadius: 1, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
      <Typography variant="body2">{explain(details)}</Typography>
      {details.criteria_source && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
          {rulesSource(details)}
          {details.prompt_level && <> · Mastery prompt level {promptLabel(details.prompt_level)}</>}
          {details.mode === 'recompute' && <> · Recomputed after a rule change</>}
          {details.mode === 'recheck' && <> · Re-checked after a rule change</>}
        </Typography>
      )}
      {ev.length > 0 && (
        <Table size="small" sx={{ mt: 1 }}>
          <TableHead>
            <TableRow>
              <TableCell>Session date</TableCell>
              <TableCell>Data sheet</TableCell>
              <TableCell align="right">Result</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ev.map((e, i) => (
              <TableRow key={`${e.session_id || 'x'}-${e.sheet}-${i}`}>
                <TableCell>{e.date || '—'}</TableCell>
                <TableCell>{SHEET_LABELS[e.sheet] || e.sheet}</TableCell>
                <TableCell align="right">{formatValue(e.value)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};

WhyPanel.propTypes = { details: PropTypes.object.isRequired };

const HistoryEntry = ({ entry }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const isEngine = entry.source !== 'manual';
  const statusChanged = entry.from_status !== entry.to_status && (entry.from_status || entry.to_status);
  const phaseChanged = entry.from_phase !== entry.to_phase;
  const label = (code, name, map) => name || map[code] || (code ? code.replace(/_/g, ' ') : '—');
  const canExplain = isEngine && entry.details && entry.details.rule;
  const isRecheck = isEngine && entry.details?.mode === 'recheck';

  return (
    <Stack direction="row" spacing={1.5} sx={{ py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
      <Tooltip title={isEngine ? 'Rule engine' : 'Changed by a person'}>
        <Box
          sx={{
            width: 32,
            height: 32,
            flexShrink: 0,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(isEngine ? theme.palette.info.main : theme.palette.warning.main, 0.12),
            color: isEngine ? 'info.main' : 'warning.dark'
          }}
          aria-label={isEngine ? 'Rule engine' : 'Changed by a person'}
        >
          {isEngine ? <RobotOutlined /> : <UserOutlined />}
        </Box>
      </Tooltip>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {isRecheck && (
            <>
              <Typography variant="body2" fontWeight={600}>
                Re-checked under new rules
              </Typography>
              <Typography variant="body2">·</Typography>
              <Chip
                size="small"
                color={entry.details.outcome === 'still_mastered' ? 'success' : 'default'}
                variant="outlined"
                label={entry.details.outcome === 'still_mastered' ? 'Still mastered' : 'Still not mastered'}
              />
            </>
          )}
          {statusChanged && (
            <>
              <Chip size="small" variant="outlined" label={label(entry.from_status, entry.from_status_name, {})} />
              <Typography variant="body2">→</Typography>
              <Chip size="small" color="primary" label={label(entry.to_status, entry.to_status_name, {})} />
            </>
          )}
          {phaseChanged && (
            <Typography variant="body2" color="text.secondary">
              {statusChanged ? '· ' : ''}
              {label(entry.from_phase, null, PHASE_LABELS)} → {label(entry.to_phase, null, PHASE_LABELS)}
            </Typography>
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
          {formatWhen(entry.changed_at)} · {isEngine ? 'Rule engine' : entry.actor_name || 'A person'}
          {isEngine && entry.actor_name && (
            <>
              {' '}
              (
              {entry.details?.mode === 'recompute' || entry.details?.mode === 'recheck'
                ? `after ${entry.actor_name} changed the mastery rules`
                : `after ${entry.actor_name}'s session`}
              )
            </>
          )}
          {entry.session_date && <> · Session {entry.session_date}</>}
        </Typography>
        {entry.reason && !canExplain && (
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {entry.reason}
          </Typography>
        )}
        {canExplain && (
          <>
            <Link component="button" type="button" variant="body2" onClick={() => setOpen((o) => !o)} sx={{ mt: 0.5 }}>
              {open ? 'Hide why' : 'Why did the rule engine do this?'}
            </Link>
            <Collapse in={open} unmountOnExit>
              <WhyPanel details={entry.details} />
            </Collapse>
          </>
        )}
      </Box>
    </Stack>
  );
};

HistoryEntry.propTypes = { entry: PropTypes.object.isRequired };

/**
 * TARGET STATUS HISTORY - a small icon that opens the target's mastery status
 * change history: every change by the rule engine (robot icon) or a person
 * (person icon), and for the rule engine a "why" link explaining the rule, the
 * criteria source, the mastery prompt level and the sessions that met it.
 *
 * Shown next to the status in Goals & Targets and in the Target Tracking sheet.
 * Renders nothing for targets that are not saved yet (no row id). Needs the
 * network, so the icon is disabled offline - the sheet itself keeps working.
 */
const TargetStatusHistoryButton = ({ targetId, targetName = '', size = 'small' }) => {
  const intl = useIntl();
  const { isOnline } = useOnlineStatus();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [entries, setEntries] = useState([]);

  if (!targetId || !UUID_RE.test(String(targetId))) return null;

  const title = intl.formatMessage({ id: 'targetHistory.title', defaultMessage: 'Status history' });

  const handleOpen = async (e) => {
    e.stopPropagation();
    setOpen(true);
    setLoading(true);
    setError('');
    try {
      const res = await getTargetStatusHistory(targetId);
      setEntries(res?.entries || []);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Could not load the history');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Tooltip
        title={isOnline ? title : intl.formatMessage({ id: 'targetHistory.offline', defaultMessage: 'Status history needs a connection' })}
      >
        {/* The wrapper takes the click when the button is disabled (offline): stop it
            reaching a clickable table row behind it. */}
        <span onClick={(e) => e.stopPropagation()} role="presentation">
          <IconButton size={size} onClick={handleOpen} disabled={!isOnline} aria-label={title}>
            <HistoryOutlined />
          </IconButton>
        </span>
      </Tooltip>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        onClick={(e) => e.stopPropagation()}
        maxWidth="sm"
        fullWidth
        aria-labelledby="target-history-title"
      >
        <DialogTitle id="target-history-title">
          {title}
          {targetName && (
            <Typography variant="body2" color="text.secondary">
              {targetName}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {loading && (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          )}
          {!loading && error && <Alert severity="error">{error}</Alert>}
          {!loading && !error && entries.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              {intl.formatMessage({ id: 'targetHistory.empty', defaultMessage: 'No status changes recorded for this target yet.' })}
            </Typography>
          )}
          {!loading && !error && entries.map((entry) => <HistoryEntry key={entry.id} entry={entry} />)}
          {!loading && !error && entries.length > 0 && (
            <Stack direction="row" spacing={2} sx={{ pt: 1.5 }}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <RobotOutlined />
                <Typography variant="caption">Rule engine</Typography>
              </Stack>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <UserOutlined />
                <Typography variant="caption">Person</Typography>
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{intl.formatMessage({ id: 'common.close', defaultMessage: 'Close' })}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

TargetStatusHistoryButton.propTypes = {
  targetId: PropTypes.string,
  targetName: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium'])
};

export default TargetStatusHistoryButton;
